/*
 * Purpose: Onboarding Wizard — first-run vault, keys, Google Cloud, Gmail, LinkedIn
 * Last modified: 2026-06-28 | Agent
 * Completeness: 93
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { PRODUCT_TERMS, PRODUCT_BRAND } from '@/lib/productTerms';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { useSecretStore } from '@/stores/secretStore';
import { connectGmailOAuth } from '@/services/connectors/gmailOAuth';
import { fetchLatestGmailApiMessage } from '@/services/connectors/gmailApi';
import { DEFAULT_JOB_GMAIL_QUERY } from '@/services/connectors/gmailSource';
import {
  hasLinkedInOAuthConfig,
  isLinkedInConnected,
  startLinkedInOAuth,
} from '@/services/connectors/linkedinOAuth';
import { parseSecretsPaste } from '@/services/setup/parseSecretsPaste';
import {
  getFirstActionableStep,
  isCoreSetupComplete,
  isStepComplete,
  markSetupCompleted,
  readSetupSnapshot,
  SETUP_STEPS,
  setupProgressPercent,
  type SetupStepId,
} from '@/services/setup/setupStatus';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  Mail,
  Shield,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';

const GOOGLE_CONSOLE = 'https://console.cloud.google.com/';
const GMAIL_API_LIB = 'https://console.cloud.google.com/apis/library/gmail.googleapis.com';
const OAUTH_CONSENT = 'https://console.cloud.google.com/apis/credentials/consent';
const OAUTH_CREDS = 'https://console.cloud.google.com/apis/credentials';

const LOCAL_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';

export function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStepId>('welcome');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const {
    isUnlocked,
    hasEncryptedVault,
    legacySecretsPending,
    isSaving,
    createVault,
    unlockVault,
    setSecret,
    refreshVaultStatus,
  } = useSecretStore();

  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pasteBlock, setPasteBlock] = useState('');
  const [openAiKey, setOpenAiKey] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [linkedInId, setLinkedInId] = useState('');
  const [linkedInSecret, setLinkedInSecret] = useState('');
  const [gmailQuery, setGmailQuery] = useState(DEFAULT_JOB_GMAIL_QUERY);
  const [googleChecklist, setGoogleChecklist] = useState({
    project: false,
    gmailApi: false,
    consent: false,
    clientId: false,
  });

  useEffect(() => {
    refreshVaultStatus();
  }, [refreshVaultStatus, tick]);

  const snap = useMemo(() => readSetupSnapshot(), [tick, isUnlocked]);

  const progress = setupProgressPercent(snap);
  const stepIndex = SETUP_STEPS.findIndex((s) => s.id === step);

  const goNext = () => {
    const idx = SETUP_STEPS.findIndex((s) => s.id === step);
    if (idx < SETUP_STEPS.length - 1) setStep(SETUP_STEPS[idx + 1].id);
  };

  const goBack = () => {
    const idx = SETUP_STEPS.findIndex((s) => s.id === step);
    if (idx > 0) setStep(SETUP_STEPS[idx - 1].id);
  };

  const jumpToFirstIncomplete = () => {
    setStep(getFirstActionableStep(snap));
  };

  const copyText = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleCreateVault = async () => {
    setBusy(true);
    try {
      await createVault(passphrase, confirm);
      setPassphrase('');
      setConfirm('');
      toast.success('Vault created');
      refresh();
      goNext();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlock = async () => {
    setBusy(true);
    try {
      await unlockVault(passphrase);
      setPassphrase('');
      toast.success('Vault unlocked');
      refresh();
      goNext();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Wrong passphrase');
    } finally {
      setBusy(false);
    }
  };

  const saveSecret = async (name: string, value: string) => {
    if (!value.trim()) return;
    await setSecret(name, value.trim());
    refresh();
  };

  const handlePasteImport = async () => {
    if (!isUnlocked) {
      toast.error('Unlock vault first');
      return;
    }
    const parsed = parseSecretsPaste(pasteBlock);
    if (parsed.length === 0) {
      toast.error('No KEY=value lines found');
      return;
    }
    setBusy(true);
    try {
      for (const { name, value } of parsed) {
        await setSecret(name, value);
      }
      toast.success(`Imported ${parsed.length} secret(s)`);
      setPasteBlock('');
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveOpenAi = async () => {
    if (!openAiKey.trim()) return;
    setBusy(true);
    try {
      await saveSecret('OPENAI_API_KEY', openAiKey);
      setOpenAiKey('');
      toast.success('OpenAI key saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveGoogleId = async () => {
    if (!googleClientId.trim()) return;
    setBusy(true);
    try {
      await saveSecret('GOOGLE_CLIENT_ID', googleClientId);
      setGoogleClientId('');
      setGoogleChecklist((c) => ({ ...c, clientId: true }));
      toast.success('Google Client ID saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleConnectGmail = async () => {
    setBusy(true);
    try {
      await connectGmailOAuth();
      toast.success('Gmail connected');
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Connect failed');
    } finally {
      setBusy(false);
    }
  };

  const handleTestGmail = async () => {
    setBusy(true);
    try {
      const msg = await fetchLatestGmailApiMessage(gmailQuery);
      if (!msg) toast.message('No messages matched — try a broader query');
      else toast.success(`Found: ${msg.subject}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLinkedIn = async () => {
    setBusy(true);
    try {
      if (linkedInId.trim()) await saveSecret('LINKEDIN_CLIENT_ID', linkedInId);
      if (linkedInSecret.trim()) await saveSecret('LINKEDIN_CLIENT_SECRET', linkedInSecret);
      setLinkedInId('');
      setLinkedInSecret('');
      toast.success('LinkedIn credentials saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = () => {
    markSetupCompleted();
    navigate('/compose');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-12">
      {/* Step rail — navigation, full contrast */}
      <div className="ui-nav ui-surface-nav rounded-xl px-4 py-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="ui-type-nav text-foreground">
            Step {Math.max(1, stepIndex)} of {SETUP_STEPS.length}
          </p>
          <Badge variant={isCoreSetupComplete(snap) ? 'default' : 'secondary'}>{progress}% ready</Badge>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
          {SETUP_STEPS.map((s) => {
            const done = isStepComplete(s.id, snap);
            const active = s.id === step;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(s.id)}
                title={s.label}
                className={cn(
                  'shrink-0 flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] sm:text-xs border transition-colors duration-200',
                  active && 'ui-step-active',
                  !active && done && 'ui-step-dim border-ab-green-sea/40 text-ab-green-deep dark:text-ab-green-glow',
                  !active && !done && 'ui-step-dim border-border'
                )}
              >
                {done ? (
                  <CheckCircle2 className="h-3 w-3 shrink-0 ui-icon-ok" />
                ) : (
                  <Circle className="h-3 w-3 shrink-0" />
                )}
                <span>{s.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {step === 'welcome' && (
        <Card className="ui-surface-focus ui-motion-object-interactive">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wand2 className="h-6 w-6 ui-icon" />
              <CardTitle className="ui-type-title">Welcome to the Onboarding Wizard</CardTitle>
            </div>
            <CardDescription className="text-foreground ui-type-instruction">
              One-time platform setup: encrypted vault, API keys, Google Cloud OAuth, and Gmail.
              The {PRODUCT_TERMS.autoBlockFlowComposer} assembles {PRODUCT_TERMS.blocks.toLowerCase()}{' '}
              into {PRODUCT_TERMS.autoBlockFlow}s — use {PRODUCT_TERMS.autoBlockFlowSetup} there for
              per-flow credentials.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2">
              <li className="ui-tier-hero flex gap-2">
                <Shield className="h-4 w-4 shrink-0 ui-icon" />
                Create an encrypted vault for API keys
              </li>
              <li className="ui-tier-hero flex gap-2">
                <KeyRound className="h-4 w-4 shrink-0 ui-icon" />
                Add OpenAI + Google credentials (paste-friendly)
              </li>
              <li className="ui-tier-hero flex gap-2">
                <Mail className="h-4 w-4 shrink-0 ui-icon" />
                Connect your real Gmail — no separate inbox
              </li>
            </ul>
            {isCoreSetupComplete(snap) ? (
              <div className="ui-tier-soft">
                Core onboarding looks complete. Jump to the end or revisit any step.
              </div>
            ) : (
              <Button onClick={jumpToFirstIncomplete} className="w-full sm:w-auto">
                {snap.isUnlocked ? 'Continue onboarding' : 'Start onboarding'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'vault' && (
        <Card className="ui-surface-focus">
          <CardHeader>
            <CardTitle className="ui-type-title flex items-center gap-2">
              <Shield className="h-5 w-5 ui-icon" />
              Secrets vault
            </CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              One passphrase protects all API keys on this device. You will need it each session.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {legacySecretsPending && (
              <p className="ui-tier-soft ui-surface-inset rounded-md p-3">
                Old unencrypted secrets detected — creating a vault will migrate them automatically.
              </p>
            )}
            {!hasEncryptedVault ? (
              <>
                <div className="group">
                  <Label className="ui-tier-hero">Passphrase (min 8 characters)</Label>
                  <Input
                    type="password"
                    className="mt-1 max-w-[275px] w-full"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="group">
                  <Label className="ui-tier-hero">Confirm passphrase</Label>
                  <Input
                    type="password"
                    className="mt-1 max-w-[275px] w-full"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <Button onClick={() => void handleCreateVault()} disabled={busy || isSaving}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create vault
                </Button>
              </>
            ) : !isUnlocked ? (
              <>
                <div className="group">
                  <Label className="ui-tier-hero">Vault passphrase</Label>
                  <Input
                    type="password"
                    className="mt-1 max-w-[275px] w-full"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <Button onClick={() => void handleUnlock()} disabled={busy || isSaving}>
                  Unlock vault
                </Button>
              </>
            ) : (
              <div className="ui-tier-soft ui-tier-active flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 ui-icon-ok" />
                Vault unlocked — continue to add keys.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'secrets' && (
        <Card className="ui-surface-focus">
          <CardHeader>
            <CardTitle className="ui-type-title flex items-center gap-2">
              <KeyRound className="h-5 w-5 ui-icon" />
              API keys
            </CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              Paste a block of KEY=value lines (from your notes file) or enter keys one at a time.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isUnlocked && (
              <p className="ui-tier-soft">Unlock the vault on the previous step first.</p>
            )}
            <div className="group">
              <Label className="ui-tier-hero">Bulk paste (OPENAI_API_KEY=… per line)</Label>
              <Textarea
                className="mt-1 font-mono text-xs min-h-[100px]"
                placeholder={'OPENAI_API_KEY=sk-...\nLINKEDIN_CLIENT_ID=...\nLINKEDIN_CLIENT_SECRET=...'}
                value={pasteBlock}
                onChange={(e) => setPasteBlock(e.target.value)}
                disabled={!isUnlocked}
              />
              <Button
                variant="outline"
                size="sm"
                className="mt-2 ui-tier-secondary-btn"
                onClick={() => void handlePasteImport()}
                disabled={!isUnlocked || busy || !pasteBlock.trim()}
              >
                Import all lines
              </Button>
            </div>
            <div className="border-t pt-4 group">
              <Label className="ui-tier-hero">OpenAI API key (recommended for AI planning)</Label>
              <div className="ui-field-action-group ui-field-action-group--stack mt-1">
                <Input
                  type="password"
                  placeholder="sk-..."
                  value={openAiKey}
                  onChange={(e) => setOpenAiKey(e.target.value)}
                  disabled={!isUnlocked}
                  className="font-mono text-sm flex-1 min-w-0"
                />
                <Button
                  className="ui-field-action-btn shrink-0"
                  onClick={() => void handleSaveOpenAi()}
                  disabled={!isUnlocked || busy || !openAiKey.trim()}
                >
                  Save
                </Button>
              </div>
              {snap.hasOpenAi && (
                <p className="ui-tier-faint ui-tier-active mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 ui-icon-ok" /> OPENAI_API_KEY saved
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'google' && (
        <Card className="ui-surface-focus">
          <CardHeader>
            <CardTitle className="ui-type-title">Google Cloud (one-time)</CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              Google requires a free project so {PRODUCT_BRAND} can read Gmail with your permission. You need
              an <strong>OAuth Web client</strong> — not a service account (service accounts cannot read
              personal Gmail in the browser).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="ui-tier-soft space-y-1 border-l-2 border-ab-ink-300/50 pl-3">
              <p className="font-medium">Service account vs OAuth client</p>
              <p>
                If you created a <strong>service account</strong> + JSON key, that is for server
                automation (e.g. Workspace admin). {PRODUCT_BRAND} uses <strong>OAuth client ID (Web)</strong>{' '}
                so you click Connect Gmail and sign in as yourself.
              </p>
            </div>
            <div className="space-y-3 text-sm">
              {[
                {
                  key: 'project' as const,
                  label: 'Create or select a Google Cloud project',
                  href: GOOGLE_CONSOLE,
                },
                {
                  key: 'gmailApi' as const,
                  label: 'Enable Gmail API',
                  href: GMAIL_API_LIB,
                },
                {
                  key: 'consent' as const,
                  label: 'OAuth consent screen — add gmail.readonly scope + yourself as test user',
                  href: OAUTH_CONSENT,
                },
                {
                  key: 'clientId' as const,
                  label: 'Credentials → Create OAuth client ID → type Web application (not service account)',
                  href: OAUTH_CREDS,
                },
              ].map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-3 py-2 cursor-pointer"
                >
                  <Checkbox
                    checked={googleChecklist[item.key]}
                    onCheckedChange={(v) =>
                      setGoogleChecklist((c) => ({ ...c, [item.key]: Boolean(v) }))
                    }
                    className="mt-0.5"
                  />
                  <span
                    className={cn(
                      'ui-tier-soft flex-1 min-w-0',
                      googleChecklist[item.key] && 'ui-tier-active'
                    )}
                  >
                    {item.label}
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 inline-flex items-center text-inherit text-xs underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open <ExternalLink className="h-3 w-3 ml-0.5" />
                    </a>
                  </span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <p className="ui-tier-soft font-medium">Authorized JavaScript origin (copy this):</p>
              <div className="ui-field-action-group ui-copy-field">
                <Input readOnly value={LOCAL_ORIGIN} className="text-xs font-mono flex-1 min-w-0" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="ui-field-action-btn ui-btn-copy"
                  onClick={() => void copyText(LOCAL_ORIGIN, 'Origin')}
                  aria-label="Copy origin"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="group">
              <Label className="ui-tier-hero">Paste OAuth Client ID (.apps.googleusercontent.com)</Label>
              <div className="ui-field-action-group ui-field-action-group--stack mt-1">
                <Input
                  placeholder="123456789-abc.apps.googleusercontent.com"
                  value={googleClientId}
                  onChange={(e) => setGoogleClientId(e.target.value)}
                  disabled={!isUnlocked}
                  className="font-mono text-xs flex-1 min-w-0"
                />
                <Button
                  className="ui-field-action-btn shrink-0"
                  onClick={() => void handleSaveGoogleId()}
                  disabled={!isUnlocked || busy || !googleClientId.trim()}
                >
                  Save to vault
                </Button>
              </div>
              {snap.hasGoogleClientId && (
                <p className="ui-tier-faint ui-tier-active mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 ui-icon-ok" /> GOOGLE_CLIENT_ID saved
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'gmail' && (
        <Card className="ui-surface-focus">
          <CardHeader>
            <CardTitle className="ui-type-title flex items-center gap-2">
              <Mail className="h-5 w-5 ui-icon" />
              Connect your Gmail
            </CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              Signs into your existing account. Job alerts stay in your normal inbox — we only read
              messages matching your search query.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!snap.hasGoogleClientId && (
              <p className="ui-tier-soft ui-surface-inset rounded-md p-3">
                Complete the Google Cloud step and save GOOGLE_CLIENT_ID first.
              </p>
            )}
            <Button
              onClick={() => void handleConnectGmail()}
              disabled={busy || !isUnlocked || !snap.hasGoogleClientId}
              className="w-full sm:w-auto"
            >
              <Mail className="h-4 w-4 mr-2" />
              {snap.gmailConnected ? 'Reconnect Gmail' : 'Connect Gmail'}
            </Button>
            {snap.gmailConnected && (
              <>
                <p className="ui-tier-faint ui-tier-active flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 ui-icon-ok" /> Connected — token saved as GOOGLE_ACCESS_TOKEN
                </p>
                <div className="group">
                  <Label className="ui-tier-soft">Test search query</Label>
                  <Input
                    className="mt-1 font-mono text-xs"
                    value={gmailQuery}
                    onChange={(e) => setGmailQuery(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 ui-tier-secondary-btn"
                    onClick={() => void handleTestGmail()}
                    disabled={busy}
                  >
                    Test fetch from my inbox
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'linkedin' && (
        <Card className="ui-surface-focus">
          <CardHeader>
            <CardTitle className="ui-type-title">LinkedIn (optional)</CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              For job-hunt flows that tailor using your LinkedIn profile. Skip if you only need Gmail
              triggers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <a
              href="https://www.linkedin.com/developers/apps"
              target="_blank"
              rel="noreferrer"
              className="ui-interactive ui-tier-soft underline inline-flex items-center"
            >
              LinkedIn Developer portal <ExternalLink className="h-3 w-3 ml-1" />
            </a>
            <div className="group">
              <Label className="ui-tier-hero">LINKEDIN_CLIENT_ID</Label>
              <Input
                className="mt-1 font-mono text-xs"
                value={linkedInId}
                onChange={(e) => setLinkedInId(e.target.value)}
                disabled={!isUnlocked}
              />
            </div>
            <div className="group">
              <Label className="ui-tier-hero">LINKEDIN_CLIENT_SECRET</Label>
              <Input
                type="password"
                className="mt-1 font-mono text-xs"
                value={linkedInSecret}
                onChange={(e) => setLinkedInSecret(e.target.value)}
                disabled={!isUnlocked}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="ui-tier-secondary-btn"
                onClick={() => void handleSaveLinkedIn()}
                disabled={!isUnlocked || busy}
              >
                Save credentials
              </Button>
              <Button
                onClick={() => {
                  try {
                    startLinkedInOAuth('/connect/setup');
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : 'OAuth failed');
                  }
                }}
                disabled={!hasLinkedInOAuthConfig()}
              >
                Connect LinkedIn
              </Button>
            </div>
            {isLinkedInConnected() && (
              <p className="ui-tier-faint ui-tier-active flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 ui-icon-ok" /> LinkedIn connected
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'done' && (
        <Card className="ui-surface-focus border-border/50">
          <CardHeader>
            <CardTitle className="ui-type-title flex items-center gap-2">
              <Sparkles className="h-5 w-5 ui-icon" />
              You are ready
            </CardTitle>
            <CardDescription className="text-foreground ui-type-instruction">
              Start describing outcomes in the {PRODUCT_TERMS.autoBlockFlowComposer} to assemble an{' '}
              {PRODUCT_TERMS.autoBlockFlow}, or open the Builder.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2">
              {SETUP_STEPS.filter((s) => s.id !== 'welcome' && s.id !== 'done').map((s) => (
                <li
                  key={s.id}
                  className={cn(
                    'ui-tier-soft flex items-center gap-2',
                    isStepComplete(s.id, snap) && 'ui-tier-active'
                  )}
                >
                  {isStepComplete(s.id, snap) ? (
                    <CheckCircle2 className="h-4 w-4 ui-icon-ok" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span>
                    {s.label}
                    {s.optional ? ' (optional)' : ''}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={finishSetup} className="flex-1">
                Describe an outcome
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(REMEDIATION.mission)}
                className="flex-1 ui-tier-secondary-btn"
              >
                Open {PRODUCT_TERMS.mission}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nav footer — navigation, full contrast */}
      {step !== 'welcome' && (
        <div className="ui-nav ui-surface-nav rounded-xl px-4 py-3 flex justify-between gap-2">
          <Button variant="secondary" className="ui-tier-tertiary-btn" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          {step !== 'done' && (
            <Button
              onClick={goNext}
              variant={isStepComplete(step, snap) ? 'default' : 'secondary'}
              className={!isStepComplete(step, snap) ? 'ui-tier-tertiary-btn' : undefined}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
